import React, { useState, useEffect } from 'react';
import {
    Package,
    Search,
    DollarSign,
    CheckCircle,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Target
} from 'lucide-react';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { proveedorService } from '../../services/maintenance/proveedorService';
import toast from 'react-hot-toast';

interface PaqueteComparacion {
    id: number;
    nombre: string;
    descripcion: string;
    proveedor: {
        id: number;
        razon_social: string;
        calificacion: number;
    };
    precio_paquete: number;
    precio_individual: number;
    ahorro: number;
    descuento_porcentaje: number;
    items_coinciden: number;
    is_explicit?: boolean;
    total_items_rutina: number;
    items_incluidos: any[];
    items_faltantes: any[];
    es_completo: boolean;
}

interface Plan {
    id: number;
    nombre: string;
    modelo_vehiculo?: string;
}

interface Rutina {
    id: number;
    nombre: string;
    frecuencia_km?: number;
}

const ResumenPaquetes: React.FC = () => {
    const [planes, setPlanes] = useState<Plan[]>([]);
    const [rutinas, setRutinas] = useState<Rutina[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [selectedRutinaId, setSelectedRutinaId] = useState<string>('');
    const [paquetes, setPaquetes] = useState<PaqueteComparacion[]>([]);
    const [searching, setSearching] = useState(false);
    const [expandedPackageId, setExpandedPackageId] = useState<number | null>(null);

    useEffect(() => {
        loadPlanes();
    }, []);

    const loadPlanes = async () => {
        try {
            const data = await maintenanceService.getPlanesMaestros('company');
            setPlanes(data);
        } catch (error) {
            console.error('Error al cargar planes:', error);
            toast.error('Error al cargar planes maestros');
        }
    };

    const handlePlanChange = async (planId: string) => {
        setSelectedPlanId(planId);
        setSelectedRutinaId('');
        setRutinas([]);
        setPaquetes([]);
        setExpandedPackageId(null);
        if (!planId) return;

        try {
            const response = await maintenanceService.getPlanMaestroById(Number(planId));
            if (response.success && response.data.rutinas) {
                setRutinas(response.data.rutinas);
            }
        } catch (error) {
            console.error('Error al cargar rutinas:', error);
            toast.error('Error al cargar rutinas del plan');
        }
    };

    const handleComparar = async () => {
        if (!selectedRutinaId) {
            toast.error('Selecciona una rutina para comparar');
            return;
        }

        try {
            setSearching(true);
            setExpandedPackageId(null);
            const response = await proveedorService.getPaquetesParaRutina(Number(selectedRutinaId));
            if (response.success) {
                setPaquetes(response.data);
                if (response.data.length === 0) {
                    toast('No se encontraron precios para los ítems de esta rutina.', {
                        icon: 'ℹ️',
                        duration: 4000
                    });
                } else {
                    toast.success(`Se encontraron ${response.data.length} opciones de comparación`);
                }
            }
        } catch (error) {
            console.error('Error al comparar paquetes:', error);
            toast.error('Error al buscar paquetes compatibles');
        } finally {
            setSearching(false);
        }
    };

    const toggleDetails = (id: number) => {
        setExpandedPackageId(current => current === id ? null : id);
    };

    const getCoverageBadge = (coinciden: number, total: number) => {
        const percentage = total > 0 ? (coinciden / total) * 100 : 0;
        let colorClass = 'bg-red-50 text-red-700 border-red-100';
        let label = 'Baja Cobertura';

        if (percentage === 100) {
            colorClass = 'bg-green-50 text-green-700 border-green-100';
            label = 'Cobertura Completa';
        } else if (percentage >= 50) {
            colorClass = 'bg-yellow-50 text-yellow-700 border-yellow-100';
            label = 'Cobertura Parcial';
        }

        return (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${colorClass} inline-flex items-center gap-1.5`}>
                <Target size={12} />
                {label} ({percentage.toFixed(0)}%)
            </span>
        );
    };

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-8">
            {/* Header Section */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Comparativa de Paquetes</h2>
                <p className="text-gray-500 mt-1">Analiza la mejor opción costo-beneficio para tus rutinas de mantenimiento.</p>
            </div>

            {/* Selection Panel */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    <div className="md:col-span-5">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Maestro</label>
                        <div className="relative">
                            <select
                                className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white transition-all appearance-none outline-none text-sm"
                                value={selectedPlanId}
                                onChange={(e) => handlePlanChange(e.target.value)}
                            >
                                <option value="">Seleccione un plan de mantenimiento...</option>
                                {planes.map(plan => (
                                    <option key={plan.id} value={plan.id}>{plan.nombre} - {plan.modelo_vehiculo || 'Sin modelo'}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        </div>
                    </div>

                    <div className="md:col-span-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Rutina a Cotizar</label>
                        <div className="relative">
                            <select
                                className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white transition-all appearance-none outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                value={selectedRutinaId}
                                onChange={(e) => setSelectedRutinaId(e.target.value)}
                                disabled={!selectedPlanId}
                            >
                                <option value="">{selectedPlanId ? 'Seleccione rutina...' : 'Primero seleccione un plan'}</option>
                                {rutinas.map(rutina => (
                                    <option key={rutina.id} value={rutina.id}>
                                        {rutina.nombre} ({rutina.frecuencia_km ? `${rutina.frecuencia_km} km` : 'Intervalo manual'})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <button
                            onClick={handleComparar}
                            disabled={!selectedRutinaId || searching}
                            className={`w-full py-2.5 rounded-lg font-medium text-white shadow-sm transition-all flex items-center justify-center gap-2 text-sm ${!selectedRutinaId || searching
                                ? 'bg-gray-300 cursor-not-allowed transform-none'
                                : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow hover:-translate-y-0.5'
                                }`}
                        >
                            {searching ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span>Buscando...</span>
                                </>
                            ) : (
                                <>
                                    <Search size={18} />
                                    <span>Comparar Precios</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Area */}
            <div className="space-y-6">
                {paquetes.length > 0 && (
                    <div className="flex items-center justify-between px-1">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Package className="text-indigo-600" size={20} />
                            Paquetes Disponibles <span className="text-gray-400 font-normal text-sm ml-1">({paquetes.length} encontrados)</span>
                        </h3>
                    </div>
                )}

                {paquetes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
                        {paquetes.map((paquete) => (
                            <div
                                key={paquete.id}
                                className={`bg-white rounded-xl shadow-sm border transition-all duration-200 overflow-hidden flex flex-col ${expandedPackageId === paquete.id ? 'border-indigo-300 ring-4 ring-indigo-50/50' : 'border-gray-200 hover:border-indigo-200 hover:shadow-md'
                                    }`}
                            >
                                {/* Card Header / Main Info */}
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="flex justify-between items-start mb-2 gap-2">
                                        <div>
                                            <h4 className="text-lg font-bold text-gray-900 leading-tight mb-0.5">{paquete.nombre}</h4>
                                            <p className="text-indigo-600 font-medium text-xs flex items-center gap-1">
                                                {paquete.proveedor.razon_social}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 transform scale-90 origin-top-right flex flex-col items-end gap-1.5">
                                            {getCoverageBadge(paquete.items_coinciden, paquete.total_items_rutina)}
                                            {paquete.is_explicit ? (
                                                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase rounded border border-indigo-100">Paquete</span>
                                            ) : (
                                                <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-[10px] font-bold uppercase rounded border border-gray-200">Calculado</span>
                                            )}
                                        </div>
                                    </div>

                                    <p className="text-gray-500 text-xs line-clamp-2 mb-4 min-h-[2.5em]">
                                        {paquete.descripcion || 'Sin descripción adicional.'}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto mb-4">
                                        <div className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 px-2.5 py-1 rounded-md text-xs font-semibold border border-gray-100">
                                            <CheckCircle size={12} className="text-indigo-500" />
                                            {paquete.items_coinciden} / {paquete.total_items_rutina}
                                        </div>
                                        {paquete.items_faltantes.length > 0 && (
                                            <div className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-md text-xs font-semibold border border-red-100">
                                                <AlertCircle size={12} className="text-red-500" />
                                                Faltan {paquete.items_faltantes.length}
                                            </div>
                                        )}
                                    </div>

                                    <div className="pt-4 border-t border-gray-100 flex flex-col items-end">
                                        {paquete.ahorro > 0 && (
                                            <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                                <DollarSign size={10} strokeWidth={3} />
                                                Ahorro: S/ {parseFloat(String(paquete.ahorro)).toFixed(2)}
                                            </div>
                                        )}
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-xs text-gray-400 font-medium">S/</span>
                                            <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                                                {parseFloat(String(paquete.precio_paquete)).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer Action */}
                                <div className="px-5 pb-5">
                                    <button
                                        onClick={() => toggleDetails(paquete.id)}
                                        className={`w-full py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${expandedPackageId === paquete.id
                                            ? 'bg-indigo-50 text-indigo-700 ring-2 ring-indigo-100'
                                            : 'bg-gray-50 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                                            }`}
                                    >
                                        {expandedPackageId === paquete.id ? 'Ocultar Detalle' : 'Ver Detalle'}
                                        {expandedPackageId === paquete.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                </div>

                                {/* Expanded Details */}
                                {expandedPackageId === paquete.id && (
                                    <div className="border-t border-gray-100 bg-gray-50/50 p-4 animate-in slide-in-from-top-2 duration-200 text-sm">
                                        <div className="mb-4">
                                            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                <CheckCircle size={12} className="text-green-500" />
                                                Incluidos ({paquete.items_incluidos.length})
                                            </h5>
                                            {paquete.items_incluidos.length > 0 ? (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {paquete.items_incluidos.map((item, idx) => {
                                                        const isRequired = item.es_requerido !== false;
                                                        const hasQuantityIssue = isRequired && !item.cumple_cantidad;
                                                        const isSurplus = isRequired && item.cantidad > (item.cantidad_requerida || 0);

                                                        let bgClass = 'bg-gray-50 border-gray-200 text-gray-400'; // Extra
                                                        let dotClass = 'bg-gray-300';

                                                        if (isRequired) {
                                                            if (hasQuantityIssue) {
                                                                bgClass = 'bg-amber-50 border-amber-200 text-amber-800'; // Insufficient
                                                                dotClass = 'bg-amber-500';
                                                            } else if (isSurplus) {
                                                                bgClass = 'bg-blue-50 border-blue-200 text-blue-800'; // Surplus
                                                                dotClass = 'bg-blue-500';
                                                            } else {
                                                                bgClass = 'bg-white border-gray-200 text-gray-700'; // Exact
                                                                dotClass = 'bg-green-500';
                                                            }
                                                        }

                                                        return (
                                                            <div
                                                                key={`inc-${idx}`}
                                                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border shadow-sm text-xs ${bgClass}`}
                                                                title={`${item.nombre} ${isRequired ? `(Req: ${item.cantidad_requerida || '?'})` : '(Extra)'}`}
                                                            >
                                                                {/* Dot indicator */}
                                                                <div className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></div>

                                                                <span className="font-medium max-w-[120px] truncate">
                                                                    {item.nombre}
                                                                </span>

                                                                <span className="text-[10px] font-mono opacity-80 ml-1">
                                                                    {isRequired ? (
                                                                        <>
                                                                            <span className={hasQuantityIssue ? 'text-red-600 font-bold' : ''}>x{item.cantidad}</span>
                                                                            <span className="text-gray-400 mx-0.5">/</span>
                                                                            <span>{item.cantidad_requerida}</span>
                                                                        </>
                                                                    ) : (
                                                                        <span>x{item.cantidad}</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400 italic">Ninguno especificado.</p>
                                            )}
                                        </div>

                                        <div>
                                            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                <AlertCircle size={12} className="text-red-500" />
                                                Faltantes ({paquete.items_faltantes.length})
                                            </h5>
                                            {paquete.items_faltantes.length > 0 ? (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {paquete.items_faltantes.map((item, idx) => (
                                                        <div key={`falt-${idx}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-red-100 bg-red-50 text-xs text-red-800">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-red-400"></div>
                                                            <span className="font-medium max-w-[120px] truncate" title={item.nombre}>{item.nombre}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-2 bg-green-50 rounded border border-green-100/50">
                                                    <span className="text-xs font-bold text-green-700 flex items-center justify-center gap-1">
                                                        <CheckCircle size={12} /> Completo
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    /* Info / Empty States */
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                        {!selectedRutinaId ? (
                            <div className="text-center max-w-md animate-in fade-in zoom-in-95 duration-300">
                                <div className="bg-indigo-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                                    <Search className="text-indigo-500" size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Comienza tu análisis</h3>
                                <p className="text-gray-500 leading-relaxed">
                                    Selecciona un <strong>Plan Maestro</strong> y una <strong>Rutina de Mantenimiento</strong> arriba para buscar paquetes de proveedores que cubran tus necesidades.
                                </p>
                            </div>
                        ) : !searching && (
                            <div className="text-center max-w-md animate-in fade-in zoom-in-95 duration-300">
                                <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-100">
                                    <Package className="text-gray-300" size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">No se encontraron resultados</h3>
                                <p className="text-gray-500 mb-6">
                                    Ningún proveedor tiene un paquete activo que coincida con los ítems de esta rutina.
                                </p>
                                <button
                                    className="text-indigo-600 font-medium hover:text-indigo-800 text-sm hover:underline"
                                    onClick={() => setSelectedRutinaId('')}
                                >
                                    Intentar con otra rutina
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResumenPaquetes;
